import {
  isVanDerValkSuitabilityConfigRow,
  normalizeVanDerValkSuitabilityRow,
  type VanDerValkSuitabilityConfig,
} from './types'

/** Must match `grouping._unwrap_list_payload` in django/oslib/hardware_integrations/vandervalk. */
const SUITABILITY_LIST_PAYLOAD_KEYS = ['suitableSystems', 'suitable_systems', 'suitableSystemList'] as const

type CommonAndVariants = {
  common: Record<string, unknown>
  variants: unknown[]
}

function isCommonAndVariants(x: unknown): x is CommonAndVariants {
  if (x == null || typeof x !== 'object') return false
  const o = x as CommonAndVariants
  return 'common' in o && o.common != null && typeof o.common === 'object' && Array.isArray(o.variants)
}

function mergeCommonVariant(
  common: Record<string, unknown>,
  part: Record<string, unknown>
): VanDerValkSuitabilityConfig {
  const merged = { ...common, ...part } as unknown as VanDerValkSuitabilityConfig
  return normalizeVanDerValkSuitabilityRow(merged)
}

function expandOneGrouped(commonAndVariants: CommonAndVariants, displayLabel?: string): VanDerValkSuitabilityConfig[] {
  const { common, variants } = commonAndVariants
  const label = displayLabel?.trim() || undefined
  const out: VanDerValkSuitabilityConfig[] = []
  for (const v of variants) {
    if (v == null || typeof v !== 'object') continue
    const row = mergeCommonVariant(common, v as Record<string, unknown>)
    out.push(label ? { ...row, displayLabel: label } : row)
  }
  return out
}

/** Grouped vendor map `{ [displayLabel]: { common, variants } }` (insertion order preserved). */
function isGroupedVendorSystemsMap(o: Record<string, unknown>): boolean {
  const entries = Object.entries(o)
  if (entries.length === 0) {
    return false
  }
  return entries.every(([, v]) => isCommonAndVariants(v))
}

function expandGroupedVendorSystemsMap(o: Record<string, unknown>): VanDerValkSuitabilityConfig[] {
  const out: VanDerValkSuitabilityConfig[] = []
  for (const [displayLabel, grouped] of Object.entries(o)) {
    if (!isCommonAndVariants(grouped)) {
      continue
    }
    out.push(...expandOneGrouped(grouped, displayLabel))
  }
  return out
}

/**
 * If the API returns `{ common, variants[] }` entries (or a map of those), expand each
 * to full `VanDerValkSuitabilityConfig` rows for grouping / UI.
 */
function tryExpandArray(arr: unknown[]): VanDerValkSuitabilityConfig[] | null {
  if (arr.length === 0) return []
  if (isCommonAndVariants(arr[0])) {
    return arr.filter(isCommonAndVariants).flatMap((g) => expandOneGrouped(g))
  }
  if (arr.every(isVanDerValkSuitabilityConfigRow)) {
    return arr.map((r) => normalizeVanDerValkSuitabilityRow(r as VanDerValkSuitabilityConfig))
  }
  return null
}

export function expandVanDerValkSuitabilityData(data: unknown): VanDerValkSuitabilityConfig[] {
  if (data == null) return []

  if (Array.isArray(data)) {
    const out = tryExpandArray(data)
    if (out != null) return out
  }

  if (typeof data === 'object' && !Array.isArray(data)) {
    const o = data as Record<string, unknown>
    for (const key of SUITABILITY_LIST_PAYLOAD_KEYS) {
      if (key in o) {
        return expandVanDerValkSuitabilityData(o[key])
      }
    }
    if (isGroupedVendorSystemsMap(o)) {
      return expandGroupedVendorSystemsMap(o)
    }
  }

  return []
}

/**
 * One-shot normaliser for the suitability POST response: flat rows and grouped common/variants.
 * Returns `null` if we cannot build at least one valid row.
 */
export function normalizeSuitabilityResultPayload(data: unknown): VanDerValkSuitabilityConfig[] | null {
  if (data == null) return null
  if (Array.isArray(data) && data.length > 0) {
    if (data.every(isVanDerValkSuitabilityConfigRow)) {
      return (data as VanDerValkSuitabilityConfig[]).map(normalizeVanDerValkSuitabilityRow)
    }
  }
  const expanded = expandVanDerValkSuitabilityData(data)
  if (expanded.length > 0) {
    return expanded
  }
  if (Array.isArray(data) && data.length === 0) {
    return []
  }
  return null
}
